"use client";

import React from "react";
import { ArrowRight, Check, MapPin } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface CommunityArea {
  id: string;
  name: string;
  county: string;
  focus: string;
  leadTime: string;
}
const communities: CommunityArea[] = [{
  id: "brookhaven",
  name: "Brookhaven",
  county: "DeKalb County",
  focus: "Tree canopy conservation & lot-coverage variance standards",
  leadTime: "Verified municipal permitting"
}, {
  id: "chamblee",
  name: "Chamblee",
  county: "DeKalb County",
  focus: "Mid-century modern expansions & zoning setback compliance",
  leadTime: "Streamlined inspection process"
}, {
  id: "dunwoody",
  name: "Dunwoody",
  county: "DeKalb County",
  focus: "Traditional footprint additions & architectural review standards",
  leadTime: "Full zoning clearance"
}, {
  id: "buckhead",
  name: "Buckhead",
  county: "Fulton County",
  focus: "Luxury interior estates, structural walls & historic zoning",
  leadTime: "Architectural board ready"
}, {
  id: "sandy-springs",
  name: "Sandy Springs",
  county: "Fulton County",
  focus: "Riparian buffer reviews, custom pools & whole-home remodels",
  leadTime: "Complete plan approval"
}, {
  id: "doraville",
  name: "Doraville",
  county: "DeKalb County",
  focus: "Interior load-bearing reconfiguration & modernized basements",
  leadTime: "Expedited contractor filings"
}];
export default function Coverage() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 15
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="service-area" data-nav="light" className="relative overflow-hidden bg-background py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-9 lg:px-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Column: 5 Cols Narrative */}
          <motion.div initial={{
          opacity: shouldReduceMotion ? 1 : 0,
          y: shouldReduceMotion ? 0 : 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.25
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex flex-col justify-start lg:col-span-5">
            <div className="inline-flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              <span className="font-sans text-xs font-semibold tracking-widest text-primary uppercase">
                Community Coverage
              </span>
            </div>

            <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-[2.65rem] lg:leading-[1.18]">
              Rooted in Brookhaven, built for North Atlanta.
            </h2>

            <div className="mt-6 h-px w-24 bg-primary/40" />

            <p className="mt-6 font-sans text-base leading-relaxed text-foreground sm:text-lg">
              Every neighborhood has its own rhythm, soil, and building board. We work directly
              with DeKalb and Fulton County municipal inspectors, historic review officers, and
              zoning staff. From tree canopy regulations to lot-coverage variances, we
              handle every permit so your renovation advances without avoidable delays.
            </p>

            <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-4">
              <Button asChild variant="secondary" size="lg" className="rounded-lg px-6">
                <Link to="/ContactRequestAnEstimatePage">
                  Start a consultation
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-lg border-border">
                <Link to="/OurProjectsPage">
                  View local projects
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: 7 Cols Atelier Glass Cards */}
          <div className="lg:col-span-7">
            <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
            once: true,
            amount: 0.2
          }} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {communities.map((item, i) => <div key={item.id} data-index={i}>
                  <motion.div variants={cardVariants} whileHover={shouldReduceMotion ? {} : {
                y: -4
              }} transition={{
                duration: 0.25,
                ease: [0.2, 0.8, 0.2, 1]
              }} className="group relative flex h-full flex-col justify-between rounded-2xl border border-white/80 bg-card/85 p-6 shadow-sm backdrop-blur-md transition-[border-color,box-shadow] duration-250 hover:border-primary/40 hover:shadow-md">
                    <div>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-secondary">
                            <MapPin className="size-4 text-primary" />
                          </span>
                          <div>
                            <h3 className="font-sans text-lg font-semibold tracking-tight text-foreground">
                              {item.name}
                            </h3>
                            <span className="font-sans text-xs text-muted-foreground">
                              {item.county}
                            </span>
                          </div>
                        </div>

                        <Badge variant="secondary" className="rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-medium text-foreground">
                          <Check className="size-3 text-primary" />
                          Active
                        </Badge>
                      </div>

                      <div className="mt-4 border-t border-border/60 pt-4">
                        <p className="font-sans text-xs font-medium uppercase tracking-wider text-muted-foreground">
                          Zoning & Code Focus
                        </p>
                        <p className="mt-1 font-sans text-sm leading-relaxed text-foreground">
                          {item.focus}
                        </p>
                      </div>
                    </div>

                    <div className="mt-5 flex items-center gap-2 pt-2 text-xs font-medium text-muted-foreground">
                      <span className="size-1.5 rounded-full bg-primary" />
                      <span>{item.leadTime}</span>
                    </div>
                  </motion.div>
                </div>)}
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}